import { useUserContext } from "api/user/UserProvider";
import CorrespondenceForm from "components/correspondence-table/CorrespondenceForm";
import CorrespondenceByFilters from "components/correspondence-table/CorrespondenceByFilters";
import InfoContext from "pages/InfoContext";
import { useContext, useEffect, useMemo, useState } from "react";
import {
  CORRESPONDENCE_TABLE_CSLT_STR_FILTER_OPTIONS,
  POPUP_STATUS,
  QUERY_STATUS,
} from "constants/constants";
import DocumentDownloadModal from "components/modal/DocumentDownloadModal";
import { usePersonContext } from "api/directory/person/PersonProvider";
import { useMissionContext } from "api/mission/MissionProvider";
import { formatDateToYYYYMMDD } from "utils/formatters";
import { useJobContext } from "api/settings/job/JobProvider";
import { useJobCategoryContext } from "api/settings/job-category/JobCategoryProvider";
import { useClientContext } from "api/directory/client/ClientProvider";
import PageContext from "pages/PageContext";
import { useDashboardContext } from "api/dashboard/DashboardProvider";
import AlertPopup from "components/alert-popup/AlertPopup";
import {
  ERROR_INCONSISTENT_DATES,
  ERROR_REQUIRED_DATES,
} from "constants/popup-codes";

const CorrespondenceDashboard = () => {
  const { lang, userScope } = useUserContext();
  const { getPersonsByIds, personsList } = usePersonContext();
  const {
    correspondanceMissionsList,
    setCorrespondanceMissionsList,
    getCorrespondanceTimeline,
    loadTimelineCorrespondance,
  } = useMissionContext();
  const { downloadCorrespondenceTableExport } = useDashboardContext();
  const { jobList, jobListGetter } = useJobContext();
  const { jobCategoryList, jobCategoryListGetter } = useJobCategoryContext();
  const { getClientsByIds, clientsByIds } = useClientContext();
  const { isLinkClick, setIsLinkClick } = useContext(PageContext);
  const [startDateFilter, setStartDateFilter] = useState(
    formatDateToYYYYMMDD(new Date())
  );
  const [endDateFilter, setEndDateFilter] = useState("");
  const [csltStrFilter, setCsltStrFilter] = useState(
    CORRESPONDENCE_TABLE_CSLT_STR_FILTER_OPTIONS[0].value
  );
  const [code, setCode] = useState();
  const [status, setStatus] = useState();

  const getMoreCorrespondanceTimelineMissions = ({ page }) =>
    getCorrespondanceTimeline({
      worker_type: csltStrFilter,
      start_date: startDateFilter,
      end_date: endDateFilter,
      page,
    });

  const errorHandler = (errorCode) => {
    setCode(errorCode);
    setStatus(POPUP_STATUS.ERROR);
  };

  const search = () => {
    const hasMissingDates = !startDateFilter || !endDateFilter;
    const hasInconsistentDates = startDateFilter > endDateFilter;

    if (hasMissingDates) errorHandler(ERROR_REQUIRED_DATES);
    else if (hasInconsistentDates) errorHandler(ERROR_INCONSISTENT_DATES);
    else {
      setCorrespondanceMissionsList([]);
      getCorrespondanceTimeline({
        worker_type: csltStrFilter,
        start_date: startDateFilter,
        end_date: endDateFilter,
      });
    }
  };

  const [correspondenceExportStatus, setCorrespondenceExportStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [exportBtnClicked, setExportBtnClicked] = useState("");

  const handleDownloadExport = async ({ exportType }) => {
    if (correspondenceExportStatus === QUERY_STATUS.LOADING) return;
    setCorrespondenceExportStatus(QUERY_STATUS.LOADING);
    setExportBtnClicked(exportType);
    try {
      await downloadCorrespondenceTableExport({
        exportType,
        csltStrFilter: csltStrFilter,
        startDateFilter: startDateFilter,
        endDateFilter: endDateFilter,
      });
      setCorrespondenceExportStatus(QUERY_STATUS.SUCCESS);
    } catch (error) {
      console.error(error);
      setCorrespondenceExportStatus(QUERY_STATUS.ERROR);
    }
  };

  useEffect(() => {
    !isLinkClick && setCorrespondanceMissionsList([]);
    setIsLinkClick(false);
  }, []);

  useEffect(() => {
    jobListGetter();
    jobCategoryListGetter();
  }, []);

  useEffect(() => {
    const startDate = new Date(startDateFilter);
    const endDate = new Date(
      startDate.getFullYear(),
      startDate.getMonth() + 1,
      0
    );
    setEndDateFilter(formatDateToYYYYMMDD(endDate));
  }, [startDateFilter]);

  useEffect(() => {
    const extractAndFilterIds = (field) => {
      const ids = new Set(
        correspondanceMissionsList.content?.map((elem) => elem[field])
      );
      return Array.from(ids).filter((id) => id);
    };
    const csltStrIdsToFetch = extractAndFilterIds("worker_id");
    const mgrIdsToFetch = extractAndFilterIds("manager_id");
    const clientsIdsToFetch = extractAndFilterIds("client_id");
    const personsIds = [...csltStrIdsToFetch, ...mgrIdsToFetch];
    const clientsIds = [...clientsIdsToFetch];
    if (personsIds.length > 0) {
      getPersonsByIds({ ids: personsIds });
    }
    if (clientsIds.length > 0) {
      getClientsByIds({ ids: clientsIds });
    }
  }, [correspondanceMissionsList]);

  const values = useMemo(
    () => ({
      setStartDateFilter,
      startDateFilter,
      setEndDateFilter,
      endDateFilter,
      csltStrFilter,
      setCsltStrFilter,
      CORRESPONDENCE_TABLE_CSLT_STR_FILTER_OPTIONS,
      search,
      lang,
      userScope,
      missionsList: correspondanceMissionsList,
      loadTimelineCorrespondance,
      personsList,
      jobList,
      jobCategoryList,
      clientsByIds,
      setIsLinkClick,
      downloadExport: handleDownloadExport,
      correspondenceExportStatus,
      exportBtnClicked,
      getMoreCorrespondanceTimelineMissions,
      formatDateToYYYYMMDD,
    }),
    [
      setStartDateFilter,
      startDateFilter,
      setEndDateFilter,
      endDateFilter,
      csltStrFilter,
      setCsltStrFilter,
      CORRESPONDENCE_TABLE_CSLT_STR_FILTER_OPTIONS,
      lang,
      userScope,
      search,
      correspondanceMissionsList,
      loadTimelineCorrespondance,
      personsList,
      jobList,
      jobCategoryList,
      clientsByIds,
      setIsLinkClick,
      correspondenceExportStatus,
      exportBtnClicked,
      formatDateToYYYYMMDD,
    ]
  );
  return (
    <InfoContext.Provider value={values}>
      <DocumentDownloadModal
        status={correspondenceExportStatus}
        onClose={() => setCorrespondenceExportStatus(QUERY_STATUS.INITIAL)}
      />
      <AlertPopup
        code={code}
        setCode={setCode}
        setStatus={setStatus}
        status={status}
      />
      <CorrespondenceForm />
      <CorrespondenceByFilters />
    </InfoContext.Provider>
  );
};

export default CorrespondenceDashboard;
